import type { SavingsAccountBalance } from '@/interfaces/SavingsDeposit.interface';
import type { SavingsHubInstructionPayload } from '@/interfaces/SavingsCustomer.interface';
import type { SavingsInstruction } from '@/interfaces/SavingsInstruction.interface';
import type { SavingsPortfolioGovernance } from '@/interfaces/SavingsPortfolio.interface';
import type {
  SavingsCashAccount,
  SavingsCashAccountPayload
} from '@/interfaces/SavingsCashAccount.interface';
import {
  SAVINGS_API_VERSION_HEADERS,
  SavingsReferenceResourceClient
} from '../savings-reference-resource';

export class ApiSavingsCashAccounts extends SavingsReferenceResourceClient<
  SavingsCashAccount,
  SavingsCashAccountPayload
> {
  protected readonly path = '/savings-cash-accounts';
  protected readonly searchFilter = 'source_reference';

  async get(id: number): Promise<SavingsCashAccount> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** Raises an instruction from the Hub card (FLT-5637). */
  async raiseInstruction(
    id: number,
    payload: SavingsHubInstructionPayload
  ): Promise<SavingsInstruction> {
    const res = await this.client.post(
      `${this.path}/${id}/instructions`,
      payload,
      {
        headers: SAVINGS_API_VERSION_HEADERS
      }
    );
    return res.data.data;
  }

  /** Sets the portfolio's minimum Fitch rating; null means any (FLT-2073). */
  async updateGovernance(
    id: number,
    minimumFitchRating: string | null
  ): Promise<SavingsPortfolioGovernance> {
    const res = await this.client.put(
      `${this.path}/${id}/governance`,
      { minimum_fitch_rating: minimumFitchRating },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  async balance(id: number): Promise<SavingsAccountBalance> {
    const res = await this.client.get(`${this.path}/${id}/balance`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }
}

export { savingsCashAccountsQueryKeys } from './query-keys';
export { useSavingsCashAccountQuery } from './use-savings-cash-accounts-queries';
export {
  useSavingsCashAccountBalanceMutation,
  useSavingsPortfolioGovernanceMutation,
  useSavingsRaiseHubInstructionMutation,
  useSavingsCashAccountsCreateMutation,
  useSavingsCashAccountsDeleteMutation,
  useSavingsCashAccountsUpdateMutation
} from './use-savings-cash-accounts-mutations';
export type {
  SavingsCashAccountsUpdateVariables,
  SavingsPortfolioGovernanceVariables,
  SavingsRaiseHubInstructionVariables
} from './use-savings-cash-accounts-mutations';
